import {
  creditUtilization, lifeInsuranceNeeds, rentAffordability,
  freelancerTax, firstTimeHomeBuyer, inflationImpact, debtToIncome,
} from "../../lib/calculators/newtools";
import { CAD } from "../../lib/format";
import { PROVINCE_LIST } from "../../lib/financial-data/canada";

const PROV_OPTS = PROVINCE_LIST.map((p) => ({ value: p.code, label: p.name }));

// 1. Credit Score Improvement / Utilization ---------------------------------
export const creditUtilizationConfig = {
  slug: "credit-utilization-calculator-canada",
  name: "Credit Utilization Calculator",
  category: "debt",
  icon: "Scale",
  tagline: "See your credit utilization and how much to pay down to boost your score.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  recommendedProduct: "debt-freedom-planner",
  relatedTools: ["debt-payoff-calculator-canada", "debt-to-income-ratio-calculator-canada"],
  seo: {
    title: "Credit Utilization Calculator Canada (2026) — Improve Your Score",
    description: "Calculate your credit card utilization ratio and see exactly how much to pay down to get under 30% (and 10%) to improve your Canadian credit score.",
    keywords: ["credit utilization calculator", "credit score canada", "credit card utilization ratio", "improve credit score canada"],
  },
  inputs: [
    {
      id: "cards", label: "Your credit cards", type: "list", itemLabel: "card", addLabel: "Add card",
      columns: [
        { key: "name", label: "Card", type: "text" },
        { key: "limit", label: "Limit", type: "currency" },
        { key: "balance", label: "Balance", type: "currency" },
      ],
      default: [
        { name: "Visa", limit: 10000, balance: 3800 },
        { name: "Mastercard", limit: 5000, balance: 2600 },
      ],
    },
  ],
  compute: (v) => {
    const r = creditUtilization({ cards: v.cards || [] });
    return {
      primary: { label: "Overall utilization", value: r.utilization * 100, type: "percent" },
      secondary: [
        { label: "Total limit", value: r.totalLimit, type: "currency" },
        { label: "Total balance", value: r.totalBalance, type: "currency" },
        { label: "Pay down to hit 30%", value: r.payToThirty, type: "currency" },
        { label: "Pay down to hit 10%", value: r.payToTen, type: "currency" },
      ],
      status: {
        type: r.utilization <= 0.30 ? "success" : "warning",
        text: r.utilization <= 0.10
          ? "Excellent — under 10% utilization is ideal for your score."
          : r.utilization <= 0.30
            ? "Good — you're under the 30% guideline. Aim for under 10% for the best score impact."
            : `Paying down about ${CAD(r.payToThirty)} gets you under the 30% guideline lenders like to see.`,
      },
      chart: {
        type: "bar", title: "Utilization by card",
        data: r.perCard.map((c) => ({ x: c.name, Utilization: Math.round(c.util * 100) })),
        xKey: "x", series: [{ key: "Utilization", name: "Utilization %" }],
      },
      downloadRows: () => [["Card", "Utilization %"], ...r.perCard.map((c) => [c.name, `${(c.util * 100).toFixed(0)}%`])],
    };
  },
  content: {
    howItWorks: "Credit utilization is your total balances divided by your total credit limits — one of the biggest factors in your credit score. Canadian credit bureaus reward keeping it under 30%, and under 10% is ideal. This tool shows your ratio and exactly how much to pay down.",
    inputsExplained: [
      { term: "Limit", desc: "The maximum credit available on each card." },
      { term: "Balance", desc: "What you currently owe on each card." },
    ],
    example: "With $15,000 of limits and $6,400 of balances, your utilization is 43% — paying down about $1,900 gets you under 30%.",
    assumptions: ["Utilization is measured across all revolving credit.", "Reported balance is usually your statement balance."],
    limitations: ["Score also depends on payment history, age of credit and inquiries.", "Each bureau may weight utilization slightly differently."],
  },
  faq: [
    { q: "What utilization is best for my credit score?", a: "Under 30% is the common guideline, but under 10% typically gives the biggest boost. Paying before your statement date lowers the balance that gets reported." },
    { q: "Should I close unused cards?", a: "Usually no — closing a card lowers your total limit and can raise your utilization. Keeping it open (used occasionally) often helps your score." },
  ],
};

// 2. Life Insurance Needs ----------------------------------------------------
export const lifeInsuranceConfig = {
  slug: "life-insurance-needs-calculator-canada",
  name: "Life Insurance Needs Calculator",
  category: "budgeting",
  icon: "PiggyBank",
  tagline: "Estimate how much life insurance coverage your family actually needs.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  relatedTools: ["net-worth-calculator-canada", "debt-to-income-ratio-calculator-canada"],
  seo: {
    title: "Life Insurance Needs Calculator Canada (2026) — How Much Coverage?",
    description: "Use the DIME method to estimate how much life insurance you need in Canada: income replacement, debts, mortgage and children's education, minus savings and existing coverage.",
    keywords: ["life insurance calculator canada", "how much life insurance do i need", "DIME method", "term life coverage canada"],
  },
  inputs: [
    { id: "income", label: "Your annual income", type: "currency", default: 70000, min: 0 },
    { id: "years", label: "Years of income to replace", type: "number", default: 10, min: 0, max: 40 },
    { id: "mortgage", label: "Mortgage balance", type: "currency", default: 350000, min: 0 },
    { id: "otherDebts", label: "Other debts", type: "currency", default: 20000, min: 0 },
    { id: "children", label: "Number of children", type: "number", default: 2, min: 0, max: 12 },
    { id: "eduPerChild", label: "Education fund per child", type: "currency", default: 60000, min: 0 },
    { id: "finalExpenses", label: "Final expenses (funeral, etc.)", type: "currency", default: 25000, min: 0 },
    { id: "existingCoverage", label: "Existing life insurance", type: "currency", default: 0, min: 0 },
    { id: "savings", label: "Savings & liquid investments", type: "currency", default: 30000, min: 0 },
  ],
  compute: (v) => {
    const r = lifeInsuranceNeeds(v);
    return {
      primary: { label: "Coverage you likely need", value: r.need, type: "currency" },
      secondary: [
        { label: "Income replacement", value: r.incomeReplacement, type: "currency" },
        { label: "Mortgage", value: r.mortgage, type: "currency" },
        { label: "Other debts", value: r.otherDebts, type: "currency" },
        { label: "Children's education", value: r.education, type: "currency" },
        { label: "Final expenses", value: r.finalExpenses, type: "currency" },
        { label: "Less savings & coverage", value: r.offsets, type: "currency" },
      ],
      chart: {
        type: "pie", title: "What makes up your coverage need",
        data: [
          { name: "Income", value: Math.round(r.incomeReplacement) },
          { name: "Mortgage", value: Math.round(r.mortgage) },
          { name: "Debts", value: Math.round(r.otherDebts) },
          { name: "Education", value: Math.round(r.education) },
          { name: "Final", value: Math.round(r.finalExpenses) },
        ],
      },
      note: "This is the DIME method (Debt, Income, Mortgage, Education). Talk to a licensed advisor for a personalized recommendation.",
    };
  },
  content: {
    howItWorks: "The DIME method adds up your Debts, Income to replace, Mortgage and Education costs, then subtracts what you already have (savings + existing coverage). The result is a solid estimate of the term life insurance your family would need if you weren't there.",
    inputsExplained: [
      { term: "Years of income to replace", desc: "How many years your family would need your income — often until kids are independent." },
      { term: "Existing life insurance", desc: "Coverage you already have through work or a personal policy." },
    ],
    example: "A $70k earner with a $350k mortgage and two kids often needs $700k–$1M of term coverage.",
    assumptions: ["Coverage replaces income in today's dollars.", "Education is a lump-sum estimate per child."],
    limitations: ["Doesn't account for investment returns on the payout.", "Group coverage may end if you change jobs."],
  },
  faq: [
    { q: "Term or whole life?", a: "For most families, term life gives the most coverage per dollar during the years you need it. Whole/permanent life is usually for specific estate or tax goals — ask an advisor." },
    { q: "How often should I update this?", a: "Recalculate after big life changes — a new baby, a bigger mortgage, or paying off debt can all change how much you need." },
  ],
};

// 3. Rent Affordability ------------------------------------------------------
export const rentAffordabilityConfig = {
  slug: "rent-affordability-calculator-canada",
  name: "Rent Affordability Calculator",
  category: "budgeting",
  icon: "Home",
  tagline: "Find out how much rent you can comfortably afford each month.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  relatedTools: ["budget-calculator-canada", "debt-to-income-ratio-calculator-canada"],
  seo: {
    title: "Rent Affordability Calculator Canada (2026) — How Much Rent Can I Afford?",
    description: "Calculate how much rent you can afford in Canada based on the 30% rule and your monthly debts. See comfortable, recommended and stretch rent budgets.",
    keywords: ["rent affordability calculator canada", "how much rent can i afford", "30 percent rent rule", "rent budget calculator"],
  },
  inputs: [
    { id: "monthlyIncome", label: "Net monthly income (take-home)", type: "currency", default: 5000, min: 0 },
    { id: "monthlyDebts", label: "Other monthly debt payments", type: "currency", default: 500, min: 0, help: "Car loans, credit cards, student loans — not rent." },
  ],
  compute: (v) => {
    const r = rentAffordability(v);
    return {
      primary: { label: "Recommended max rent", value: r.affordable, type: "currency" },
      secondary: [
        { label: "Comfortable (28%)", value: r.comfortable, type: "currency" },
        { label: "Standard (30%)", value: r.rec, type: "currency" },
        { label: "Stretch (35%)", value: r.stretch, type: "currency" },
        { label: "Left after rent & debts", value: r.leftover, type: "currency" },
      ],
      status: {
        type: "info",
        text: `Keeping rent near ${CAD(r.affordable)} leaves room for savings and debt payments. Landlords often want your income to be about 3x the rent.`,
      },
    };
  },
  content: {
    howItWorks: "The classic rule is to keep housing at or below 30% of your income. This tool applies that rule and also checks that your rent plus other debts stay under 40% of income, then gives you a comfortable, standard and stretch budget.",
    inputsExplained: [
      { term: "Net monthly income", desc: "Your take-home pay after tax and deductions." },
      { term: "Other monthly debts", desc: "Recurring debt payments that reduce what you can spend on rent." },
    ],
    example: "On $5,000/month take-home with $500 of other debts, a comfortable rent is around $1,400–$1,500.",
    assumptions: ["Uses take-home (net) income.", "Assumes utilities may be extra unless included."],
    limitations: ["Doesn't include one-time move-in costs.", "High-cost cities may require a higher ratio."],
  },
  faq: [
    { q: "Is the 30% rule realistic in big cities?", a: "In cities like Toronto or Vancouver many people spend more than 30%. Use it as a target — the lower you keep housing costs, the more you can save." },
    { q: "Should utilities count as rent?", a: "If heat/hydro/internet aren't included, budget for them separately — they can add $150–$300+ a month." },
  ],
};

// 4. Freelancer / Self-Employed Tax -----------------------------------------
export const freelancerTaxConfig = {
  slug: "freelancer-self-employed-tax-calculator-canada",
  name: "Freelancer & Self-Employed Tax Calculator",
  category: "tax",
  icon: "Receipt",
  tagline: "Estimate income tax + CPP and how much to set aside as a freelancer.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  relatedTools: ["income-tax-calculator-canada", "take-home-pay-calculator-canada"].filter(Boolean),
  seo: {
    title: "Freelancer & Self-Employed Tax Calculator Canada (2026)",
    description: "Estimate your 2026 self-employment taxes in Canada: federal and provincial income tax plus both halves of CPP, and exactly how much of each invoice to set aside.",
    keywords: ["self employed tax calculator canada", "freelancer tax calculator", "how much tax to set aside self employed", "cpp self employed 2026"],
  },
  inputs: [
    { id: "grossIncome", label: "Gross self-employment income", type: "currency", default: 90000, min: 0 },
    { id: "expenses", label: "Business expenses", type: "currency", default: 15000, min: 0 },
    { id: "province", label: "Province / territory", type: "select", options: PROV_OPTS, default: "ON" },
  ],
  compute: (v) => {
    const r = freelancerTax(v);
    return {
      primary: { label: "Set aside for tax + CPP", value: r.setAsideRate * 100, type: "percent" },
      secondary: [
        { label: "Net business income", value: r.netIncome, type: "currency" },
        { label: "Income tax", value: r.incomeTax, type: "currency" },
        { label: "CPP (both halves)", value: r.cpp, type: "currency" },
        { label: "Total owing", value: r.totalTax, type: "currency" },
        { label: "After-tax income", value: r.afterTax, type: "currency" },
        { label: "Marginal rate", value: r.marginalRate * 100, type: "percent" },
      ],
      status: {
        type: "warning",
        text: `Set aside about ${(r.setAsideRate * 100).toFixed(0)}% of every payment — roughly ${CAD(r.totalTax)} this year — so you're ready at tax time. Self-employed people pay both halves of CPP.`,
      },
      chart: {
        type: "pie", title: "Where your income goes",
        data: [
          { name: "Take-home", value: Math.round(r.afterTax) },
          { name: "Income tax", value: Math.round(r.incomeTax) },
          { name: "CPP", value: Math.round(r.cpp) },
        ],
      },
      downloadRows: () => [
        ["Item", "Amount"],
        ["Net business income", Math.round(r.netIncome)],
        ["Income tax", Math.round(r.incomeTax)],
        ["CPP (both halves)", Math.round(r.cpp)],
        ["Total owing", Math.round(r.totalTax)],
        ["After-tax income", Math.round(r.afterTax)],
      ],
    };
  },
  content: {
    howItWorks: "As a freelancer you pay federal and provincial income tax on your net income (revenue minus expenses), PLUS both the employee and employer halves of CPP. This tool estimates the total and tells you what percentage of each invoice to set aside so there are no surprises at tax time.",
    inputsExplained: [
      { term: "Business expenses", desc: "Deductible costs like software, home-office, supplies and mileage that reduce your taxable income." },
      { term: "Both halves of CPP", desc: "Employees split CPP with an employer; self-employed people pay both parts themselves." },
    ],
    example: "On $90k gross with $15k expenses in Ontario, expect to set aside roughly a quarter to a third of net income for tax + CPP.",
    assumptions: ["Uses 2026 federal and provincial brackets and CPP rates.", "Assumes no HST/GST registration or instalments."],
    limitations: ["Doesn't include GST/HST, EI opt-in, or RRSP deductions.", "Estimate only — confirm with an accountant."],
  },
  faq: [
    { q: "How much should I set aside for taxes?", a: "A common rule of thumb is 25–30% of net income, but it depends on your bracket and province — use the percentage this tool calculates for your numbers." },
    { q: "Do I have to pay CPP if self-employed?", a: "Yes, on net self-employment income above the basic exemption you pay both the employee and employer portions of CPP." },
  ],
};

// 5. First-Time Home Buyer Incentive ----------------------------------------
export const firstTimeHomeBuyerConfig = {
  slug: "first-time-home-buyer-incentive-calculator-canada",
  name: "First-Time Home Buyer Incentive Calculator",
  category: "mortgage",
  icon: "Home",
  tagline: "See the down payment you need and the government programs you can stack.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  recommendedProduct: "mortgage-planner",
  relatedTools: ["mortgage-payment-calculator-canada", "mortgage-affordability-calculator-canada"].filter(Boolean),
  seo: {
    title: "First-Time Home Buyer Calculator Canada (2026) — Incentives & Down Payment",
    description: "See your minimum down payment and the first-time home buyer programs you can use in Canada: the RRSP Home Buyers' Plan ($60k), FHSA ($40k), and the $1,500 tax credit.",
    keywords: ["first time home buyer calculator canada", "home buyers plan 2026", "FHSA first home", "first time home buyer incentive canada"],
  },
  inputs: [
    { id: "price", label: "Home price", type: "currency", default: 550000, min: 0 },
    { id: "saved", label: "Amount you've saved", type: "currency", default: 40000, min: 0 },
  ],
  compute: (v) => {
    const r = firstTimeHomeBuyer(v);
    return {
      primary: { label: "Minimum down payment", value: r.minDown, type: "currency" },
      secondary: [
        { label: "Down payment %", value: r.downPct * 100, type: "percent" },
        { label: "RRSP Home Buyers' Plan", value: r.hbpMax, type: "currency", hint: "Withdraw tax-free" },
        { label: "FHSA (lifetime)", value: r.fhsaMax, type: "currency", hint: "Tax-deductible + tax-free" },
        { label: "Home Buyers' Tax Credit", value: r.hbtc, type: "currency" },
        { label: "Still to save", value: r.shortfall, type: "currency" },
      ],
      status: {
        type: r.shortfall <= 0 ? "success" : "info",
        text: r.shortfall <= 0
          ? "You've saved enough for the minimum down payment. Don't forget closing costs (~1.5–4%)."
          : `You're ${CAD(r.shortfall)} away from the minimum down payment. The FHSA and RRSP Home Buyers' Plan can help you get there faster and tax-free.`,
      },
      note: "The old federal First-Time Home Buyer Incentive (shared-equity) stopped taking applications in 2024. Today's real help is the FHSA, the RRSP Home Buyers' Plan, and the $1,500 tax credit — plus provincial land-transfer rebates.",
    };
  },
  content: {
    howItWorks: "First-time buyers in Canada can stack several programs: save up to $40,000 in an FHSA (tax-deductible going in, tax-free coming out), withdraw up to $60,000 from your RRSP under the Home Buyers' Plan, and claim a $1,500 tax credit. This tool shows your minimum down payment and what's available.",
    inputsExplained: [
      { term: "Minimum down payment", desc: "5% on the first $500k, 10% on the portion from $500k–$1.5M, 20% at $1.5M+." },
      { term: "FHSA + HBP", desc: "Two tax-advantaged ways to build and withdraw your down payment." },
    ],
    example: "On a $550,000 home the minimum down payment is $30,000 (5% of $500k + 10% of $50k).",
    assumptions: ["Uses 2026 CMHC minimum down payment tiers.", "FHSA/HBP maximums are per person — a couple can double them."],
    limitations: ["Doesn't include CMHC insurance premiums or closing costs.", "Provincial rebates vary; check your province."],
  },
  faq: [
    { q: "Can I use both the FHSA and the RRSP Home Buyers' Plan?", a: "Yes. As of 2023 you can use both for the same purchase — the FHSA (up to $40k) and the HBP (up to $60k) can be combined." },
    { q: "Is the First-Time Home Buyer Incentive still available?", a: "The federal shared-equity Incentive stopped accepting applications in 2024. The FHSA, Home Buyers' Plan and the $1,500 tax credit are the main programs now." },
  ],
};

// 6. Inflation Impact --------------------------------------------------------
export const inflationImpactConfig = {
  slug: "inflation-impact-calculator-canada",
  name: "Inflation Impact Calculator",
  category: "budgeting",
  icon: "TrendingUp",
  tagline: "See how inflation erodes your money's buying power over time.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  relatedTools: ["compound-interest-calculator-canada", "savings-goal-calculator-canada"].filter(Boolean),
  seo: {
    title: "Inflation Calculator Canada (2026) — Future Cost & Buying Power",
    description: "See how inflation changes the value of money over time in Canada. Calculate the future cost of today's expenses and how much your savings' buying power erodes.",
    keywords: ["inflation calculator canada", "purchasing power calculator", "future value of money inflation", "cost of living calculator"],
  },
  inputs: [
    { id: "amount", label: "Amount today", type: "currency", default: 50000, min: 0 },
    { id: "rate", label: "Annual inflation rate", type: "percent", default: 3, min: 0, max: 20, step: 0.1 },
    { id: "years", label: "Number of years", type: "number", default: 20, min: 1, max: 50 },
  ],
  compute: (v) => {
    const r = inflationImpact(v);
    return {
      primary: { label: `What ${CAD(v.amount)} buys in ${v.years} years (today's $)`, value: r.purchasingPower, type: "currency" },
      secondary: [
        { label: `Cost in ${v.years} years`, value: r.futureCost, type: "currency" },
        { label: "Buying power lost", value: r.lost, type: "currency" },
        { label: "Inflation rate used", value: v.rate, type: "percent" },
      ],
      chart: {
        type: "line", title: "Rising cost vs shrinking buying power",
        data: r.series, xKey: "x",
        series: [{ key: "Cost", name: "Future cost" }, { key: "Power", name: "Today's-dollar value" }],
      },
      status: { type: "info", text: `At ${v.rate}% inflation, prices roughly ${r.futureCost / v.amount >= 2 ? "more than double" : "climb steadily"} over ${v.years} years — which is why savings need to grow faster than inflation.` },
    };
  },
  content: {
    howItWorks: "Inflation means the same dollar buys less over time. This tool shows two things: how much a purchase will cost in the future, and how much the buying power of a fixed amount of cash shrinks — a reminder that money sitting idle loses value.",
    inputsExplained: [
      { term: "Annual inflation rate", desc: "The average yearly price increase. The Bank of Canada targets about 2%." },
      { term: "Buying power", desc: "What today's money would be worth in the future in today's dollars." },
    ],
    example: "At 3% inflation, $50,000 today has the buying power of about $27,700 in 20 years.",
    assumptions: ["Inflation is compounded annually at a constant rate.", "Doesn't account for investment growth."],
    limitations: ["Real inflation varies year to year.", "Your personal inflation rate may differ from the headline CPI."],
  },
  faq: [
    { q: "What inflation rate should I use?", a: "The Bank of Canada targets 2%, but using 3% is a reasonable planning buffer. Try a few rates to see the range." },
    { q: "How do I beat inflation?", a: "Keep long-term savings invested so they grow faster than inflation — cash in a low-interest account slowly loses buying power." },
  ],
};

// 7. Debt-to-Income Ratio ----------------------------------------------------
export const debtToIncomeConfig = {
  slug: "debt-to-income-ratio-calculator-canada",
  name: "Debt-to-Income Ratio Calculator",
  category: "debt",
  icon: "Scale",
  tagline: "Check your GDS and TDS ratios the way Canadian lenders do.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  relatedTools: ["debt-payoff-calculator-canada", "credit-utilization-calculator-canada"],
  seo: {
    title: "Debt-to-Income Ratio Calculator Canada (2026) — GDS & TDS",
    description: "Calculate your debt-to-income ratio (GDS and TDS) the way Canadian mortgage lenders do. See if you're within the 39%/44% guidelines and how lenders view your debt load.",
    keywords: ["debt to income ratio calculator canada", "GDS TDS calculator", "debt to income ratio mortgage", "DTI calculator canada"],
  },
  inputs: [
    { id: "monthlyIncome", label: "Gross monthly income", type: "currency", default: 7000, min: 0, help: "Before tax." },
    { id: "housing", label: "Monthly housing costs", type: "currency", default: 2200, min: 0, help: "Mortgage/rent + property tax + heat + half of condo fees." },
    { id: "otherDebts", label: "Other monthly debt payments", type: "currency", default: 700, min: 0, help: "Car loans, credit cards, lines of credit, student loans." },
  ],
  compute: (v) => {
    const r = debtToIncome(v);
    return {
      primary: { label: "Total debt-to-income (TDS)", value: r.tds * 100, type: "percent" },
      secondary: [
        { label: "Housing ratio (GDS)", value: r.gds * 100, type: "percent" },
        { label: "Total monthly debt", value: r.totalDebt, type: "currency" },
        { label: "Lender view", value: r.rating, type: "raw" },
      ],
      status: {
        type: r.tds <= 0.44 ? "success" : "warning",
        text: r.tds <= 0.44
          ? `Your TDS of ${(r.tds * 100).toFixed(0)}% is within the 44% guideline most Canadian lenders use.`
          : `Your TDS of ${(r.tds * 100).toFixed(0)}% is above the 44% lenders prefer — paying down debt or increasing income improves your borrowing power.`,
      },
      chart: {
        type: "bar", title: "Your ratios vs lender limits",
        data: [
          { x: "GDS", Yours: Math.round(r.gds * 100), Limit: 39 },
          { x: "TDS", Yours: Math.round(r.tds * 100), Limit: 44 },
        ],
        xKey: "x", series: [{ key: "Yours", name: "Yours %" }, { key: "Limit", name: "Lender limit %" }],
      },
    };
  },
  content: {
    howItWorks: "Canadian lenders use two ratios: GDS (housing costs ÷ income) and TDS (all debts ÷ income). Guidelines are about 39% for GDS and 44% for TDS. This tool calculates both so you can see your borrowing power the way a lender does.",
    inputsExplained: [
      { term: "GDS", desc: "Gross Debt Service — housing costs as a percentage of gross income." },
      { term: "TDS", desc: "Total Debt Service — all debt payments (housing + other) as a percentage of gross income." },
    ],
    example: "On $7,000/month income with $2,200 housing and $700 other debts, GDS is 31% and TDS is 41% — within lender guidelines.",
    assumptions: ["Uses gross (pre-tax) income.", "Guidelines of 39% GDS / 44% TDS follow common lender rules."],
    limitations: ["Lenders may allow higher ratios with strong credit.", "Insured mortgages can have stricter limits."],
  },
  faq: [
    { q: "What's a good debt-to-income ratio?", a: "For a mortgage, keep housing (GDS) under ~39% and total debts (TDS) under ~44%. Lower is better and gives you more room." },
    { q: "How can I improve my ratio?", a: "Pay down debts (especially high monthly payments), avoid new loans before applying, or increase your income." },
  ],
};
